// @givmo/fundraisers-module — a game's featured fundraiser on its Layers tab (FCM-WEB34; D-FCM-56, RULED
// 2026-10-22 09:14 PDT, and D-FCM-61's decision 5 as drawn): the game stores a FEATURED child fundraiser,
// never an offer (FCM-BE117); the season setup sets it to the nightly pledge fundraiser it makes; with none
// chosen, the oldest fundraiser under the game with a pledge shows first (FCM-BE121). Drawn as the card's
// level is (FCM-WEB25's decision 3): a line under the tree with Change, and a marker on the row it names.
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { sdk, type P2pLayerNodeOut } from '@givmo/api-client';
import { Button, Dialog, RadioTile, RadioTileGroup, cn, toast } from '@givmo/design-system';
import { LAYERS_COPY as C } from './layers-copy';
import { layerRefusal } from './layers-decode';
import { PHONE_HIT } from './lifecycle-ui';
import { useLivePage } from './use-live-page';
import { SEASON_COPY as S } from './season-setup-copy';
import { keepDayTogether } from './season-setup-decode';

/** The line under the tree: what the game's page shows first, and the way to change it. */
export function FeaturedLine({
  line,
  canChange,
  onChange,
}: {
  line: string;
  canChange: boolean;
  onChange: () => void;
}) {
  return (
    <div
      className="flex flex-col gap-8 border-t border-border-subtle px-20 py-12 first:border-t-0 sm:flex-row sm:items-center sm:justify-between sm:gap-16"
      data-testid="layers-featured"
    >
      <p className="text-s13 leading-h15 text-text-secondary">
        <span className="font-w600 text-text-strong">{S.featuredTitle}</span>
        {' · '}
        {line}
      </p>
      {canChange && (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          className={cn('shrink-0 self-start sm:self-auto', PHONE_HIT)}
          onClick={onChange}
          testId="layers-featured-change"
        >
          {C.cardChange}
        </Button>
      )}
    </div>
  );
}

/** Which fundraiser under the game its page shows first, or none chosen (the default rule). */
export function FeaturedDialog({
  open,
  gameId,
  chosen,
  candidates,
  onClose,
  onDone,
}: {
  open: boolean;
  gameId: string;
  /** The game's own stored choice, if it made one. */
  chosen: string | null;
  /** The fundraisers under the game it may feature, in the tree's order. */
  candidates: readonly P2pLayerNodeOut[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const live = useLivePage();
  const current = chosen ?? '';
  const [pick, setPick] = useState(current);
  useEffect(() => {
    if (open) setPick(current);
  }, [open, current]);
  const save = useMutation({
    mutationFn: () =>
      sdk.campaignsUpdateCampaign({
        path: { fundraiser_id: gameId },
        body: { featured_fundraiser_id: pick === '' ? null : pick },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await onDone();
      if (live()) {
        onClose();
        toast.success(S.settingsUpdated);
      }
    },
    onError: (error) => {
      if (live()) toast.error(layerRefusal(error).message);
    },
  });
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next && !save.isPending) onClose();
      }}
    >
      <Dialog.Content
        aria-label={S.featuredTitle}
        className="fundraisers-dialog flex flex-col gap-16"
        testId="layers-featured-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {S.featuredTitle}
        </Dialog.Title>
        <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          {S.featuredDialogBody}
        </Dialog.Description>
        <RadioTileGroup
          value={pick}
          onValueChange={setPick}
          aria-label={S.featuredTitle}
          testId="layers-featured-pick"
        >
          {candidates.map((node) => (
            <RadioTile
              key={node.campaign_id}
              value={node.campaign_id}
              label={keepDayTogether(node.campaign_name ?? C.anotherCampaign)}
              testId={`layers-featured-${node.campaign_id}`}
            />
          ))}
          <RadioTile
            value=""
            label={S.featuredNone}
            description={S.featuredNoneDesc}
            testId="layers-featured-none"
          />
        </RadioTileGroup>
        <div className="mt-8 flex items-center justify-end gap-8">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={save.isPending}
            onClick={onClose}
            testId="layers-featured-cancel"
          >
            {S.cancel}
          </Button>
          <Button
            appearance="saas"
            loading={save.isPending}
            disabled={pick === current || save.isPending}
            onClick={() => save.mutate()}
            testId="layers-featured-save"
          >
            {C.styleSave}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
